import React, { useEffect, useRef, useState } from "react";
import { Alert, BreadcrumbItem, Card, CardBody, CardHeader, Container, Nav, NavItem, NavLink, Row, Col, Button as RButton } from "reactstrap";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import * as ChatQL from "queries/chat";
import * as EntryQL from "queries/entry";
import * as ProfileQL from "queries/profile";
import { dayLabel, today } from "components/day/dates";
import { Totals, AddLink, EntryList } from "routes/day/view";
import { mealForHour } from "routes/day/new";
import { byMeal, formatNumber } from "components/day/summary";
import { useMeals } from "components/settings/meals";
import * as Replies from "components/chat/replies";
import { CupboardList } from "components/cupboard/list";
import { Mark, useMarks } from "components/chat/marks";
import { AdviceCard } from "components/chat/advice";
import { HelpButton, HelpDialog } from "components/chat/help";
import "components/chat/chat.css";
import "components/day/day.css";

// The main page: tell it what you ate, and it is logged; tell it what to fix,
// and it is changed; tell it what you have in, and the cupboard is; ask it
// what to eat, and it answers from the cupboard and what is left of the day.
// The chat on one side, and on the other two tabs, today's food log and the
// cupboard. A reply never turns the panel to another tab: the person stays
// where they chose to be, and a +N or −N beside a tab's name says it changed.
//
// Each message goes to `chat.send`. TypeSafe's Jev decides whether it is food,
// a change to today's log, a change to the cupboard, a request for advice, or
// none of these; Claude reads food into entries, a change into new values for
// today's entries or the cupboard's items, or writes the advice, and the
// server saves them. Nothing to fill in: the reply lists what was done, each
// entry a link to it, with Claude's line on the day under it, and the panel
// refetches. "Unsure" asks first, through `chat.answer`.
//
// The thread is the day's and is kept on the server (`chat.day`): a reload
// shows it again, and the models read it, so "make that lunch" has a "that".
// A new day is a new, empty thread.

export const Indice = () => <BreadcrumbItem>Chat</BreadcrumbItem>;

let sequence = 0;
const nextId = () => `local-${++sequence}`;

// After a log or a change: today's panel reads again, and every other cached
// day is dropped so the day screen does not show the log from before it.
const AFTER_LOG = {
  update: EntryQL.update(),
  refetchQueries: ["EntryDays"],
};

export const Content = () => {
  const [tab, setTab] = useState("today");
  const [marks, mark] = useMarks();
  // A reply marks what it added or took away for a few seconds
  // (components/chat/marks), and leaves the open tab as it is.
  const onReply = (message) => mark(message);

  return (
    <Container fluid className="noshchat-chat mt-4">
      <Row>
        <Col lg={7} className="noshchat-chat__pane">
          <Chat onReply={onReply} />
        </Col>
        <Col lg={5} className="mt-4 mt-lg-0">
          <SidePanel tab={tab} setTab={setTab} marks={marks} />
        </Col>
      </Row>
    </Container>
  );
};

// One line of a reply. A removed entry has nothing to link to.
const EntryLine = ({ line }) =>
  line.action === "removed" ? (
    <s data-testid={"chat-entry-removed"}>{Replies.entryLine(line)}</s>
  ) : (
    <Link to={`/entry/${line.entryId}`} data-testid={"chat-entry"}>
      {Replies.entryLine(line)}
    </Link>
  );

// One cupboard line of a reply: what the item is now, or was when it went.
const ItemLine = ({ line }) =>
  line.action === "removed" ? (
    <s data-testid={"chat-item-removed"}>{Replies.itemLine(line)}</s>
  ) : (
    <span data-testid={"chat-item"}>{Replies.itemLine(line)}</span>
  );

const Message = ({ message, onAnswer }) => {
  if (message.from === "me") {
    return (
      <div className="noshchat-chat__bubble noshchat-chat__bubble--me" data-testid={"chat-me"}>
        {message.text}
      </div>
    );
  }

  const { text, ask } = Replies.replyFor(message);
  const lines = message.entries || [];
  const items = message.items || [];
  // Advice IS the note; anything else carries it as feedback under the reply.
  const feedback = message.kind !== "advice" ? message.note : null;

  return (
    <>
      <div
        className={`noshchat-chat__bubble noshchat-chat__bubble--bot${message.kind === "advice" ? " noshchat-chat__bubble--advice" : ""}`}
        data-testid={"chat-bot"}
        data-kind={message.kind || undefined}
      >
        {message.kind === "advice" && message.advice ? <AdviceCard advice={message.advice} /> : text}
        {lines.length ? (
          <ul className="noshchat-chat__entries" data-testid={"chat-entries"}>
            {lines.map((line) => (
              <li key={`${line.action}-${line.entryId}`}>
                <EntryLine line={line} />
              </li>
            ))}
          </ul>
        ) : null}
        {items.length ? (
          <ul className="noshchat-chat__entries" data-testid={"chat-items"}>
            {items.map((line) => (
              <li key={`${line.action}-${line.itemId}`}>
                <ItemLine line={line} />
              </li>
            ))}
          </ul>
        ) : null}
        {feedback ? (
          <div className="noshchat-chat__note" data-testid={"chat-note"}>
            {feedback}
          </div>
        ) : null}
      </div>

      {ask && (
        <div className="noshchat-chat__attachment">
          <RButton color={"primary"} size={"sm"} className="mr-2" onClick={() => onAnswer(message, true)}>
            Yes, log it
          </RButton>
          <RButton color={"secondary"} outline size={"sm"} onClick={() => onAnswer(message, false)}>
            No
          </RButton>
        </div>
      )}

      {message.answered && (
        <div className="noshchat-chat__bubble noshchat-chat__bubble--me">{message.answered}</div>
      )}
    </>
  );
};

const Chat = ({ onReply = () => {} }) => {
  // The day the thread is for. Read again before every send, so a tab left
  // open past midnight starts the new day's thread rather than adding to
  // yesterday's.
  const [date, setDate] = useState(today);
  const [thread, setThread] = useState({ date: null, messages: [] });
  const [draft, setDraft] = useState("");
  const [help, setHelp] = useState(false);
  const end = useRef(null);
  const input = useRef(null);

  // An example picked in the help dialog goes in the box, for the person to
  // send or change.
  const pick = (example) => {
    setDraft(example);
    input.current && input.current.focus();
  };

  const { data, error: readError } = useQuery(ChatQL.day, {
    variables: { date },
    fetchPolicy: "network-only",
  });

  // Seeded once per day from what the server kept; after that the thread is
  // appended to as replies come back, so a late read never drops a reply.
  useEffect(() => {
    const day = data?.viewer?.account?.chat?.day;
    if (day && day.date === date && thread.date !== date) {
      setThread({ date, messages: day.messages });
    }
  }, [data, date, thread.date]);

  useEffect(() => {
    if (readError && thread.date !== date) setThread({ date, messages: [] });
  }, [readError, date, thread.date]);

  const [send, { loading: sending }] = useMutation(ChatQL.send, AFTER_LOG);
  const [answer, { loading: answering }] = useMutation(ChatQL.answer, AFTER_LOG);
  const busy = sending || answering;
  const ready = thread.date === date;

  useEffect(() => {
    end.current && end.current.scrollIntoView && end.current.scrollIntoView({ block: "end" });
  }, [thread, busy]);

  const add = (message) =>
    setThread((t) => ({ ...t, messages: [...t.messages, { id: nextId(), ...message }] }));
  const change = (id, patch) =>
    setThread((t) => ({ ...t, messages: t.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)) }));

  const submit = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || busy || !ready) return;

    // The day and the hour are the browser's, read now: a message sent at
    // 23:59 is that day's.
    const hour = new Date().getHours();
    const now = today();
    if (now !== date) {
      setDate(now);
      setThread({ date: now, messages: [] });
    }

    setDraft("");
    add({ from: "me", text });

    try {
      const { data: sent } = await send({ variables: { input: { text, hour, date: now } } });
      const reply = sent.viewer.account.chat.send;
      const kept = reply.message || { from: "bot", kind: reply.kind, entries: [] };
      add(kept);
      onReply(kept);
    } catch (e) {
      add({ from: "bot", kind: "error", entries: [] });
    }
  };

  // The unsure case, answered. The server has its own copy of what was asked
  // about, so only which question and which answer go back.
  const onAnswer = async (message, yes) => {
    change(message.id, { asking: false, answered: yes ? "Yes" : "No" });
    try {
      const { data: answered } = await answer({ variables: { input: { messageId: message.messageId, yes } } });
      const kept = answered.viewer.account.chat.answer.message;
      add(kept);
      onReply(kept);
    } catch (e) {
      add({ from: "bot", kind: "error", entries: [] });
    }
  };

  return (
    <>
      <div className="noshchat-chat__thread" data-testid={"chat-thread"}>
        <div className="noshchat-chat__bubble noshchat-chat__bubble--bot" data-testid={"chat-bot"}>
          {Replies.GREETING} <HelpButton onClick={() => setHelp(true)} label={"Examples"} />
        </div>
        <HelpDialog isOpen={help} toggle={() => setHelp((open) => !open)} onPick={pick} />

        {readError && (
          <Alert color={"warning"} className="mb-0">
            Today's conversation could not be read. New messages still work.
          </Alert>
        )}

        {!ready && !readError && (
          <div className="text-muted" data-testid={"chat-loading"}>
            <Loading.Icon />
          </div>
        )}

        {thread.messages.map((message) => (
          <Message key={message.id} message={message} onAnswer={onAnswer} />
        ))}

        {busy && (
          <div className="noshchat-chat__bubble noshchat-chat__bubble--bot text-muted" data-testid={"chat-thinking"}>
            <i className="fa fa-ellipsis-h" />
          </div>
        )}
        <div ref={end} />
      </div>

      <form className="noshchat-chat__composer" onSubmit={submit}>
        <Row noGutters className="align-items-center">
          <Col>
            <input
              className="form-control"
              aria-label={"Message"}
              placeholder={`e.g. ${Replies.EXAMPLE}`}
              ref={input}
              maxLength={500}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid={"chat-input"}
              autoFocus
            />
          </Col>
          <Col xs={"auto"} className="pl-2">
            <RButton type={"submit"} color={"primary"} disabled={!draft.trim() || busy || !ready} data-testid={"chat-send"}>
              <i className="fa fa-paper-plane" /> Send
            </RButton>
          </Col>
        </Row>
      </form>
    </>
  );
};

// Beside the chat: two tabs, today and the cupboard. One sticky column; each
// tab's own wrapper is `display: contents`, so its parts are the column's
// flex items, and only a list scrolls (chat.css).
const SidePanel = ({ tab, setTab, marks }) => (
  <div className="noshchat-day noshchat-chat__today" data-testid={"chat-side"}>
    <Nav tabs className="noshchat-chat__tabs mb-3">
      <NavItem>
        <NavLink href="#" active={tab === "today"} onClick={(e) => { e.preventDefault(); setTab("today"); }} data-testid={"chat-tab-today"}>
          Today
          <Mark marks={marks} scope={"today"} testid={"chat-tab-today-mark"} />
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink href="#" active={tab === "cupboard"} onClick={(e) => { e.preventDefault(); setTab("cupboard"); }} data-testid={"chat-tab-cupboard"}>
          Cupboard
          <Mark marks={marks} scope={"cupboard"} testid={"chat-tab-cupboard-mark"} />
        </NavLink>
      </NavItem>
    </Nav>
    {tab === "cupboard" ? <CupboardTab /> : <TodayPanel marks={marks} />}
  </div>
);

// The cupboard, the same list as its own screen.
const CupboardTab = () => (
  <div className="noshchat-chat__tab" data-testid={"chat-cupboard"}>
    {/* A flex row, not a Bootstrap .row, for the reason TodayPanel's is. */}
    <div className="d-flex align-items-center mb-2">
      <div className="flex-grow-1 mr-2 text-muted small">What you have in.</div>
      <Link className="btn btn-outline-secondary btn-sm text-nowrap" to={`/cupboard`}>
        Open the cupboard
      </Link>
    </div>
    <CupboardList compact />
  </div>
);

// Today's food log, the same totals and meals the day screen shows, measured
// against the daily target. Read once and again after every log (AFTER_LOG),
// so a message's entries appear here as its reply does. The meals are tabs
// under the totals rather than a card each, so the column stays one screen
// tall however much is logged (MealTabs).
const TodayPanel = ({ marks }) => {
  const date = today();
  const { data, loading, error } = useQuery(EntryQL.days, {
    variables: { input: { from: date, to: date } },
    fetchPolicy: "cache-and-network",
  });

  const day = data?.viewer?.account?.entries?.days?.[0];
  const targets = ProfileQL.useTargets();

  return (
    <div className="noshchat-chat__tab" data-testid={"chat-today"}>
      {/* A flex row, not a Bootstrap .row: a .row's -15px gutters overhang
          whatever holds it, and here that was a horizontal scrollbar. */}
      <div className="d-flex align-items-center">
        <div className="flex-grow-1 mr-2">
          <h5 className="m-0">Today</h5>
          <div className="text-muted small">{dayLabel(date)}</div>
        </div>
        <Link className="btn btn-outline-secondary btn-sm text-nowrap" to={`/day/${date}`}>
          Open the day
        </Link>
      </div>

      {error ? (
        <Alert color={"danger"} className="mt-3">
          The food log could not be read. {error.message}
        </Alert>
      ) : !data && loading ? (
        <div className="mt-4"><Loading.Icon /></div>
      ) : (
        <>
          <Totals totals={day?.totals} target={targets.calories} macros={targets} compact />
          <MealTabs date={date} entries={day?.entries || []} marks={marks} />
        </>
      )}
    </div>
  );
};

// Today's meals as tabs, one open at a time: its calories, an Add, and its
// entries, which scroll inside the panel while the tabs and the totals above
// them stay put. Only the meals the settings show (byMeal).
//
// Which tab is open until one is picked: the meal of the last thing logged, so
// a message's entries are in front of you the moment the panel reads again;
// on an empty day, the meal the clock is nearest to. A pick sticks.
//
// Every meal's panel is rendered and the closed ones are `hidden`, the ARIA
// tabs shape, so what each meal holds is in the page whichever is open.
const MealTabs = ({ date, entries, marks }) => {
  const shown = useMeals();
  const [picked, setPicked] = useState(null);
  if (!shown) return null;

  const groups = byMeal(entries, shown);
  const latest = [...entries].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
  const open =
    groups.find((g) => g.meal === picked) ||
    groups.find((g) => latest && g.meal === latest.meal) ||
    groups.find((g) => g.meal === mealForHour(new Date().getHours(), shown)) ||
    groups[0];

  return (
    <Card className="mt-3 noshchat-meals" data-testid={"chat-meals"}>
      <CardHeader className="noshchat-meals__header">
        <Nav tabs className="card-header-tabs" role={"tablist"}>
          {groups.map((group) => (
            <NavItem key={group.meal}>
              <NavLink
                tag={"button"}
                type={"button"}
                role={"tab"}
                id={`meal-${group.meal}-tab`}
                aria-controls={`meal-${group.meal}-panel`}
                aria-selected={group === open}
                active={group === open}
                onClick={() => setPicked(group.meal)}
                data-testid={`meal-${group.meal}-tab`}
              >
                {group.label}
                {group.items.length ? <span className="badge badge-light ml-1">{group.items.length}</span> : null}
                <Mark marks={marks} scope={`meal:${group.meal}`} testid={`meal-${group.meal}-mark`} />
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </CardHeader>
      <CardBody className="noshchat-meals__body">
        {groups.map((group) => (
          <div
            key={group.meal}
            id={`meal-${group.meal}-panel`}
            role={"tabpanel"}
            aria-labelledby={`meal-${group.meal}-tab`}
            className="noshchat-meals__panel"
            hidden={group !== open}
            data-testid={`meal-${group.meal}`}
          >
            <div className="d-flex align-items-center">
              <span className="flex-grow-1 text-muted">{formatNumber(group.calories)} kcal</span>
              <AddLink date={date} group={group} />
            </div>
            {group.items.length ? (
              <EntryList items={group.items} className="mt-1" />
            ) : (
              <div className="text-muted small mt-1">Nothing logged here yet.</div>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
};

export const Sidebar = () => <></>;
